// Gamepad device: polls the Gamepad API once per tick (no gamepadconnected/
// disconnected listeners — those events are flaky; a slot going null IS the
// disconnect signal). Chrome hands back immutable per-call snapshots while
// Firefox mutates live objects in place, so every poll re-fetches
// getGamepads() fresh rather than holding a reference across frames.

import type { Binding, DeviceSnapshot } from './actions'
import { compositeKeyVector, scaledRadialDeadzone, type Vec2 } from './deadzone'

/** Live-mutated by the tweak panel — read fields fresh every poll, never copy. */
export interface GamepadTuning {
  innerDeadzone: number
  outerDeadzone: number
}

export interface GamepadDevice extends DeviceSnapshot {
  poll(focused: boolean): void
  readonly connected: boolean
  /** false = pad present but mapping !== 'standard' (Firefox/Linux) — inputs
   *  ignored rather than guessed; UI can explain. */
  readonly recognized: boolean
  /** true for exactly the one poll after the active pad's slot went null. */
  readonly justDisconnected: boolean
  /** Haptics garnish (stretch, Phase F) — fire-and-forget; silently a no-op
   *  on browsers without vibrationActuator support (Firefox). */
  rumble(durationMs: number, weak: number, strong: number): void
}

const ZERO: Vec2 = { x: 0, y: 0 }

export function createGamepadDevice(
  tuning: GamepadTuning,
  // No Gamepad API (tests/SSR) → the default falls back to "no pads" rather
  // than throwing; an injected getGamepads (tests) bypasses this entirely.
  getGamepads: () => (Gamepad | null)[] = () =>
    typeof navigator === 'undefined' || !navigator.getGamepads ? [] : navigator.getGamepads(),
): GamepadDevice {
  let activeIndex = -1
  let activeId = ''
  let connected = false
  let recognized = false
  let justDisconnected = false
  let activityAt = -Infinity

  // Button/stick state from the most recent poll, keyed by button index.
  let pressed: boolean[] = []
  let pressedAtByIdx: number[] = []
  let leftStick: Vec2 = ZERO
  let rightStick: Vec2 = ZERO

  const stick = (axes: readonly number[], which: 'left' | 'right'): Vec2 => {
    const [ax, ay] = which === 'left' ? [axes[0], axes[1]] : [axes[2], axes[3]]
    // Browser axes: up = -1. World +y is up (see deadzone.ts's compositeKeyVector
    // comment) — invert Y so the stick and WASD agree.
    const raw: Vec2 = { x: ax ?? 0, y: -(ay ?? 0) }
    return scaledRadialDeadzone(raw, tuning.innerDeadzone, tuning.outerDeadzone)
  }

  const clearInputs = (): void => {
    pressed = []
    pressedAtByIdx = []
    leftStick = ZERO
    rightStick = ZERO
  }

  return {
    kind: 'pad',
    get connected() {
      return connected
    },
    get recognized() {
      return recognized
    },
    get justDisconnected() {
      return justDisconnected
    },
    get activityAt() {
      return activityAt
    },
    poll(focused: boolean) {
      justDisconnected = false
      const pads = getGamepads()
      const pad = activeIndex >= 0 ? (pads[activeIndex] ?? null) : (pads.find(p => p != null) ?? null)

      if (!pad) {
        if (connected) justDisconnected = true
        connected = false
        recognized = false
        activeIndex = -1
        activeId = ''
        clearInputs()
        return
      }

      // A different id at the same index is a reconnect (different controller
      // plugged into the same slot) — treat as a fresh pad, not a continuation.
      if (pad.index !== activeIndex || pad.id !== activeId) {
        activeIndex = pad.index
        activeId = pad.id
        clearInputs()
      }
      connected = true
      recognized = pad.mapping === 'standard'

      if (!recognized) {
        // Never guess axes/buttons on a non-standard mapping.
        clearInputs()
        return
      }

      if (!focused) {
        // A pad must not steer a background tab — report nothing held, but
        // connection tracking above still runs so reconnects aren't missed.
        clearInputs()
        return
      }

      const now = performance.now()
      const nextPressed: boolean[] = []
      const nextPressedAt: number[] = []
      for (let i = 0; i < pad.buttons.length; i++) {
        const down = pad.buttons[i].pressed
        nextPressed[i] = down
        const wasDown = pressed[i] ?? false
        nextPressedAt[i] = down && !wasDown ? now : (pressedAtByIdx[i] ?? -Infinity)
        if (down && !wasDown && i !== 16) activityAt = now
      }
      pressed = nextPressed
      pressedAtByIdx = nextPressedAt

      // Bump activity on stick motion past the deadzone too — drift must not
      // flip lastDevice, so only a post-deadzone magnitude counts.
      leftStick = stick(pad.axes, 'left')
      rightStick = stick(pad.axes, 'right')
      if (Math.hypot(leftStick.x, leftStick.y) > 0 || Math.hypot(rightStick.x, rightStick.y) > 0) activityAt = now
    },
    isDown(b: Binding): boolean {
      if (!('padButton' in b)) return false
      if (b.padButton === 16) return false // guide — unreliable everywhere
      return pressed[b.padButton] ?? false
    },
    pressedAt(b: Binding): number {
      if (!('padButton' in b)) return -Infinity
      if (b.padButton === 16) return -Infinity
      return pressedAtByIdx[b.padButton] ?? -Infinity
    },
    vector(b: Binding): Vec2 | null {
      if ('stick' in b) return b.stick === 'left' ? leftStick : rightStick
      if ('padDpad' in b) {
        return compositeKeyVector(pressed[12] ?? false, pressed[13] ?? false, pressed[14] ?? false, pressed[15] ?? false)
      }
      return null
    },
    rumble(durationMs: number, weak: number, strong: number) {
      if (activeIndex < 0) return
      // Re-fetch fresh — Chrome hands back immutable snapshots, so a pad
      // reference held across frames (or since the last poll) can be stale.
      const pad = getGamepads()[activeIndex]
      if (!pad) return
      void pad.vibrationActuator?.playEffect?.('dual-rumble', {
        duration: durationMs,
        weakMagnitude: weak,
        strongMagnitude: strong,
      })
    },
  }
}
